import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { doSwaggerCall } from '../../hooks/useApi';
import { describe, fetchArchiveRange, matchesTicket, sqlTime } from '../../helpers/parkingEvents';
import EventChart from './EventChart';
import EventFilters, { DEFAULT_FILTER } from './EventFilters';
import EventList from './EventList';
import { applyFilter, auditItems, bewebItem, diagnose, paymentItems } from './eventItems';

const MIN = 60e3;
const DAY = 24 * 3600e3;
// gate events (barrier opened, mode changed, ...) this close to one of the car's events are shown as context
const CONTEXT_MS = 5 * MIN;
const CONTEXT_CATS = ['device', 'operator', 'capacity'];

const ms = (s) => (s ? new Date(String(s).replace(' ', 'T')).getTime() : NaN);

// the parking's own range: entry -> exit / close-out / now (ticket ids are plates, so this is the current visit)
function parkingRange(ticket) {
    const now = Date.now();
    const entry = ms(ticket.entryTime);
    if (!entry) return { from: now - 3 * DAY, to: now, entry: null, end: null };
    // an exit / close-out before the entry belongs to the plate's previous visit: this one is still going
    const after = (t) => (t > entry ? t : NaN);
    const exit = after(ms(ticket.exitTime)) || after(ms(ticket.closedAt)) || null;
    const end = exit || now;
    return {
        from: entry - 30 * MIN,
        to: Math.min(now, end + 30 * MIN),
        entry,
        end: exit,
    };
}

// very long parkings: the first day and the last two are enough (each day is a separate download)
function loadSegments(from, to) {
    if (to - from <= 4 * DAY) return [[from, to]];
    return [
        [from, from + DAY],
        [to - 2 * DAY, to],
    ];
}

/**
 * Everything that happened to one parking: the parking system's events (from the archive),
 * gate events around them, the app payments and our audit log, on one timeline
 */
export default function TicketTimeline({ ticket, payments }) {
    const range = useMemo(
        () => parkingRange(ticket),
        [ticket.id, ticket.entryTime, ticket.exitTime, ticket.closedAt]
    );
    const [state, setState] = useState({ loading: true, events: [], audit: [], error: null });
    const [filter, setFilter] = useState(DEFAULT_FILTER);
    const [zoom, setZoom] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let stop = false;
        setState((s) => ({ ...s, loading: true, error: null }));
        Promise.all([
            Promise.all(
                loadSegments(range.from, range.to).map(([a, b]) => fetchArchiveRange(a, b))
            ).then((l) => l.flat()),
            doSwaggerCall('AuditLog', 'getAuditLogs', {
                offset: 0,
                limit: 300,
                searchTicketId: ticket.id,
                createdFrom: sqlTime(range.from),
                createdTo: sqlTime(range.to),
            })
                .then((r) => r.items || [])
                .catch(() => []),
        ])
            .then(
                ([events, audit]) =>
                    !stop && setState({ loading: false, events, audit, error: null })
            )
            .catch((error) => !stop && setState({ loading: false, events: [], audit: [], error }));
        return () => {
            stop = true;
        };
    }, [ticket.id, range.from, range.to]);

    const items = useMemo(() => {
        const own = state.events.filter((e) => matchesTicket(e, ticket.id));
        // only around the car's own events at that very gate (not "a gate it used at some point")
        const context = state.events.filter(
            (e) =>
                !e.plate &&
                CONTEXT_CATS.includes(describe(e).cat) &&
                own.some((o) => o.gate === e.gate && Math.abs(o.ts - e.ts) <= CONTEXT_MS)
        );
        const inRange = (i) => i.ts >= range.from && i.ts <= range.to;
        return [
            ...own.map((e) => bewebItem(e)),
            ...context.map((e) => bewebItem(e, true)),
            ...paymentItems(payments).filter(inRange),
            ...auditItems(state.audit, ticket.id, range.from, range.to),
        ].sort((a, b) => a.ts - b.ts);
    }, [state.events, state.audit, payments, ticket.id, range.from, range.to]);

    const shown = applyFilter(items, filter);
    const { story, findings } = useMemo(() => diagnose(items), [items]);
    const view = zoom || { from: range.from, to: range.to };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 1 }}
                flexWrap="wrap"
                useFlexGap
            >
                <Typography variant="h6">Idővonal</Typography>
                {state.loading && <CircularProgress size={18} />}
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {story.length
                        ? story.join(' · ')
                        : !state.loading &&
                          'Nincs esemény a parkoló rendszerből ehhez a parkoláshoz.'}
                </Typography>
                <ButtonGroup size="small">
                    {range.entry && (
                        <Button
                            onClick={() =>
                                setZoom({
                                    from: range.entry - 5 * MIN,
                                    to: (range.end || Date.now()) + 5 * MIN,
                                })
                            }
                        >
                            Parkolás
                        </Button>
                    )}
                    {range.end && (
                        <Button
                            onClick={() =>
                                setZoom({ from: range.end - 30 * MIN, to: range.end + 10 * MIN })
                            }
                        >
                            Kihajtás előtt
                        </Button>
                    )}
                    <Button onClick={() => setZoom(null)} disabled={!zoom}>
                        Teljes
                    </Button>
                </ButtonGroup>
            </Stack>

            {state.error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    Az esemény archívum nem érhető el: {state.error.message}
                </Alert>
            )}
            {findings.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    {findings.map((f) => (
                        <Alert
                            key={f.text}
                            severity={{ fail: 'error', warn: 'warning' }[f.sev] || 'info'}
                            sx={{ py: 0, cursor: 'pointer' }}
                            onClick={() => setSelected(f.focus)}
                        >
                            {f.text}
                        </Alert>
                    ))}
                </Stack>
            )}

            <EventChart
                items={shown}
                from={view.from}
                to={view.to}
                band={range.entry ? { from: range.entry, to: range.end } : null}
                selected={selected}
                onSelect={setSelected}
                onZoom={(a, b) => setZoom(a ? { from: a, to: b } : null)}
            />
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                Húzással nagyíts, dupla kattintással vissza. ◆ hiba, ○ figyelmeztetés; a
                halványabbak az autó kapuinál közben történt események.
            </Typography>

            <Box sx={{ mb: 1.5 }}>
                <EventFilters items={items} filter={filter} onChange={setFilter} showContext />
            </Box>
            <EventList items={shown} selected={selected} onSelect={setSelected} />
        </Paper>
    );
}
